import React from 'react';
import { Music, Search, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { useSanityProducts } from '../hooks/useSanityProducts';
import { useTranslation } from '../context/TranslationContext';
import forscoreText from '../data/forscoreText';
import { bookKeyGroup, bookPlatform, isIndexedBook } from '../utils/bookGrouping';
import { SampleDownloadCard } from '../components/SampleDownloadCard';
import PlatformReviews from '../components/PlatformReviews';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

var videoEmbed = 'https://www.youtube.com/embed/x2NzDvDG3ms';
var installVideoEmbed = 'https://www.youtube.com/embed/r8IM0aEQ_ZY';

function ForScorePage() {
  var ref = useSanityProducts({});
  var products = ref.products;
  var loading = ref.loading;
  var langRef = useTranslation();
  var language = langRef.language;
  var tx = forscoreText[language] || forscoreText.en;

  var instrumentGroups = [
    { key: 'bb', title: tx.bb, subtitle: tx.bbSub, match: 'bb' },
    { key: 'eb', title: tx.eb, subtitle: tx.ebSub, match: 'eb' },
    { key: 'bass', title: tx.bass, subtitle: tx.bassSub, match: 'bass' },
    { key: 'c', title: tx.c, subtitle: tx.cSub, match: 'c' },
    { key: 'other', title: tx.other || 'Fake Books & Collections', subtitle: tx.otherSub || 'Indexed fake books, omnibooks and method books for forScore', match: 'other' },
    { key: 'stamps', title: tx.stamps || 'ForScore Stamps', subtitle: tx.stampsSub || 'Custom annotation stamps for forScore', match: 'stamps' }
  ];

  var forscoreProducts = [];
  for (var i = 0; i < products.length; i++) {
    if (isIndexedBook(products[i]) && bookPlatform(products[i]) === 'forscore') {
      forscoreProducts.push(products[i]);
    }
  }

  function getGroupProducts(match) {
    var result = [];
    for (var j = 0; j < forscoreProducts.length; j++) {
      if (bookKeyGroup(forscoreProducts[j]) === match) result.push(forscoreProducts[j]);
    }
    result.sort(function(a, b) { return a.name.localeCompare(b.name); });
    return result;
  }

  var sections = [];
  for (var g = 0; g < instrumentGroups.length; g++) {
    var group = instrumentGroups[g];
    var groupProducts = getGroupProducts(group.match);
    if (groupProducts.length === 0) continue;

    var cards = [];
    for (var p = 0; p < groupProducts.length; p++) {
      cards.push(React.createElement(ProductCard, { key: groupProducts[p].slug, product: groupProducts[p], index: p }));
    }

    sections.push(
      React.createElement('div', { key: group.key, className: 'mb-16', 'data-testid': 'forscore-section-' + group.key },
        React.createElement('div', { className: 'flex items-center gap-4 mb-3' },
          React.createElement(Music, { className: 'w-6 h-6 text-[#d4af37]' }),
          React.createElement('h2', { className: 'text-2xl md:text-3xl font-bold text-white' }, group.title),
          React.createElement('span', { className: 'px-3 py-1 rounded-full bg-neutral-800 text-neutral-400 text-sm' }, groupProducts.length)
        ),
        React.createElement('p', { className: 'text-neutral-500 text-sm mb-6 ml-10' }, group.subtitle),
        React.createElement('div', { className: 'h-px bg-gradient-to-r from-[#d4af37]/40 via-[#d4af37]/20 to-transparent mb-8' }),
        React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' }, cards)
      )
    );
  }

  return React.createElement(Layout, null,
    React.createElement(SEO, { title: PAGE_SEO['forscore'].title, description: PAGE_SEO['forscore'].description, url: '/forscore' }),
    React.createElement('section', { className: 'pt-32 pb-12 bg-gradient-to-b from-neutral-900 to-black' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12 text-center' },
        React.createElement('p', { className: 'text-sm tracking-widest uppercase text-[#d4af37] mb-4' }, tx.digitalLibrary),
        React.createElement('h1', { className: 'text-5xl md:text-7xl font-bold text-white mb-6' }, tx.title),
        React.createElement('p', { className: 'text-xl text-neutral-400 max-w-2xl mx-auto' }, tx.subtitle)
      )
    ),
    React.createElement('section', { className: 'pb-20 bg-black' },
      React.createElement('div', { className: 'max-w-5xl mx-auto px-6 lg:px-12' },
        React.createElement(PlatformReviews, { platform: 'forscore' }),
        React.createElement(SampleDownloadCard, { platform: 'forscore', variant: 'full' }),
        React.createElement('div', { className: 'mb-12' },
          React.createElement('iframe', { src: videoEmbed, title: 'ForScore Indexed Real Books', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true })
        ),
        // How to install the ForScore-indexed files — added Feb 2026.
        React.createElement('div', { className: 'mb-12' },
          React.createElement('h2', { className: 'text-2xl md:text-3xl font-bold text-white mb-4 text-center' }, 'How to Install Your ForScore-Indexed Files'),
          React.createElement('iframe', { src: installVideoEmbed, title: 'How to install ForScore indexed files', className: 'w-full aspect-video rounded-2xl bg-neutral-900', frameBorder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowFullScreen: true })
        ),
        React.createElement('div', { className: 'max-w-3xl mx-auto' },
          React.createElement('h2', { className: 'text-3xl font-bold text-white mb-2' }, tx.indexedTitle),
          React.createElement('p', { className: 'text-[#d4af37] text-lg mb-6' }, tx.findInstantly),
          React.createElement('p', { className: 'text-neutral-300 text-lg mb-4 leading-relaxed' }, tx.desc),
          React.createElement('p', { className: 'text-neutral-400 mb-2' }, tx.noMore),
          React.createElement('p', { className: 'text-red-400/80 text-sm mb-8 font-medium' }, tx.exclusiveNote),
          React.createElement('h3', { className: 'text-xl font-bold text-white mb-4' }, tx.howItWorks),
          React.createElement('div', { className: 'space-y-3 mb-8' },
            React.createElement('div', { className: 'flex items-center gap-3' },
              React.createElement(Search, { className: 'w-5 h-5 text-[#d4af37] flex-shrink-0' }),
              React.createElement('span', { className: 'text-neutral-300' }, tx.step1)
            ),
            React.createElement('div', { className: 'flex items-center gap-3' },
              React.createElement(Zap, { className: 'w-5 h-5 text-[#d4af37] flex-shrink-0' }),
              React.createElement('span', { className: 'text-neutral-300' }, tx.step2)
            ),
            React.createElement('div', { className: 'flex items-center gap-3' },
              React.createElement(Music, { className: 'w-5 h-5 text-[#d4af37] flex-shrink-0' }),
              React.createElement('span', { className: 'text-neutral-300' }, tx.step3)
            )
          ),
          React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-8 mb-8' },
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
              React.createElement('h3', { className: 'text-lg font-bold text-white mb-4' }, tx.perfectFor),
              React.createElement('div', { className: 'space-y-2' },
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.pf1),
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.pf2),
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.pf3),
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.pf4)
              )
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
              React.createElement('h3', { className: 'text-lg font-bold text-white mb-4' }, tx.whyNeed),
              React.createElement('div', { className: 'space-y-2' },
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.wn1),
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.wn2),
                React.createElement('p', { className: 'text-neutral-300 text-sm' }, '\u2022 ' + tx.wn3)
              )
            )
          ),
          React.createElement('p', { className: 'text-center text-xl text-white font-semibold' }, tx.turnYour),
          React.createElement('p', { className: 'text-center text-[#d4af37] text-lg mt-2' }, tx.playMore)
        )
      )
    ),
    React.createElement('section', { 'data-testid': 'forscore-page', className: 'py-16 bg-black border-t border-white/5' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12' },
        loading
          ? React.createElement('div', { className: 'text-center py-24' },
              React.createElement('p', { className: 'text-neutral-500' }, '...')
            )
          : React.createElement('div', null, sections)
      )
    )
  );
}

export default ForScorePage;
